import React from "react";

const SECTION_SELECTOR = "form, .modal-content, .tab-pane, .card, .nk-block";
const CREDENTIAL_HINT_REGEX = /(email|user|username|login|confirm|password)/i;
const SKIPPED_PATHS = [
  /^\/$/i,
  /^\/login\/?$/i,
  /^\/forgot\/?$/i,
  /^\/password_setting\/?/i,
  /^\/nsaccess\/?$/i,
  /^\/admin_/i,
];

class AutofillGuard extends React.Component {
  componentDidMount() {
    this.scanQueued = false;
    this.scheduleScan();
    this.scanTimer = window.setTimeout(() => this.scheduleScan(), 500);
    this.observer = new MutationObserver(() => {
      this.scheduleScan();
    });
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scanTimer) {
      window.clearTimeout(this.scanTimer);
    }
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
    }
  }

  scheduleScan = () => {
    if (this.scanQueued) {
      return;
    }

    this.scanQueued = true;
    this.rafId = window.requestAnimationFrame(() => {
      this.scanQueued = false;
      this.scanAndProtect();
    });
  };

  getDescriptor = (input) => {
    const label =
      input
        .closest(".form-group, .form-control-group, .custom-control, .MuiFormControl-root")
        ?.querySelector("label")?.textContent || "";
    return [
      input.name,
      input.id,
      input.placeholder,
      input.getAttribute("aria-label"),
      label,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  };

  unlockInput = (event) => {
    const input = event.currentTarget || event.target;
    input.readOnly = false;
    input.removeAttribute("readonly");
    input.dataset.codexAutofillUnlocked = "true";
  };

  protectInput = (input, autocompleteValue) => {
    if (!input || input.type === "hidden" || input.disabled) {
      return;
    }

    if (input.getAttribute("autocomplete") !== autocompleteValue) {
      input.setAttribute("autocomplete", autocompleteValue);
    }
    if (input.getAttribute("data-lpignore") !== "true") {
      input.setAttribute("data-lpignore", "true");
    }
    if (input.getAttribute("data-1p-ignore") !== "true") {
      input.setAttribute("data-1p-ignore", "true");
    }
    if (input.getAttribute("autocapitalize") !== "none") {
      input.setAttribute("autocapitalize", "none");
    }
    if (input.getAttribute("autocorrect") !== "off") {
      input.setAttribute("autocorrect", "off");
    }
    if (input.getAttribute("spellcheck") !== "false") {
      input.setAttribute("spellcheck", "false");
    }

    if (!input.dataset.codexAutofillBound) {
      input.addEventListener("focus", this.unlockInput, true);
      input.addEventListener("mousedown", this.unlockInput, true);
      input.addEventListener("touchstart", this.unlockInput, true);
      input.dataset.codexAutofillBound = "true";
    }

    if (
      input.dataset.codexAutofillUnlocked !== "true" &&
      document.activeElement !== input
    ) {
      if (!input.readOnly) {
        input.readOnly = true;
      }
      if (!input.hasAttribute("readonly")) {
        input.setAttribute("readonly", "readonly");
      }
    }
  };

  ensureDecoys = (section) => {
    if (!section || section.querySelector("[data-codex-autofill-decoy='true']")) {
      return;
    }

    const decoyWrap = document.createElement("div");
    decoyWrap.setAttribute("data-codex-autofill-decoy", "true");
    decoyWrap.setAttribute("aria-hidden", "true");
    decoyWrap.style.position = "absolute";
    decoyWrap.style.opacity = "0";
    decoyWrap.style.pointerEvents = "none";
    decoyWrap.style.height = "0";
    decoyWrap.style.overflow = "hidden";

    const userInput = document.createElement("input");
    userInput.type = "text";
    userInput.name = "reseller_decoy_username";
    userInput.tabIndex = -1;
    userInput.autocomplete = "username";

    const passwordInput = document.createElement("input");
    passwordInput.type = "password";
    passwordInput.name = "reseller_decoy_password";
    passwordInput.tabIndex = -1;
    passwordInput.autocomplete = "current-password";

    decoyWrap.appendChild(userInput);
    decoyWrap.appendChild(passwordInput);

    section.insertBefore(decoyWrap, section.firstChild);
  };

  scanAndProtect = () => {
    const pathname = window.location.pathname || "";
    if (SKIPPED_PATHS.some((pattern) => pattern.test(pathname))) {
      return;
    }

    const passwordInputs = Array.from(
      document.querySelectorAll(
        "input[type='password'], input[name*='password' i]",
      ),
    );

    passwordInputs.forEach((passwordInput) => {
      const section =
        passwordInput.closest(SECTION_SELECTOR) || passwordInput.parentElement;

      this.ensureDecoys(section);
      this.protectInput(passwordInput, "section-reseller-guard new-password");

      if (!section) {
        return;
      }

      const peerInputs = Array.from(
        section.querySelectorAll("input[type='text'], input[type='email']"),
      ).filter((input) => {
        const descriptor = this.getDescriptor(input);
        return CREDENTIAL_HINT_REGEX.test(descriptor);
      });

      peerInputs.forEach((input) => {
        this.protectInput(input, "section-reseller-guard off");
      });
    });
  };

  render() {
    return null;
  }
}

export default AutofillGuard;
